import { Reducer } from "redux";
import {
	AssessmentsActions,
	AssessmentsActionTypes
} from "@/Actions/Assessments/";
import {
	Assessment,
	AssessmentVulnerabilityData,
	CloudAssessmentType,
	Ms365Assessment,
	RiskMatrixCellData
} from "@/Contracts/Assessments";

export interface AssessmentsState {
	// Manual assessments and assessments added to the document
	readonly isAssessmentLoading: boolean;
	readonly isAssessmentLogLoading: boolean;
	readonly assessmentList: Assessment[];
	readonly currentActiveAssessment: Assessment | null;
	readonly manualAssessmentDefaults: AssessmentVulnerabilityData[];
	readonly assessmentRiskMatrixData: RiskMatrixCellData[] | null;
	readonly assessmentRiskBarScore: number | null;
	readonly assessmentListingData: AssessmentVulnerabilityData[] | null;

	// MS 365 Assessments
	readonly isMs365AssessmentsLoading: boolean;
	readonly ms365AssessmentsModalOpen: boolean;
	readonly ms365AssessmentsList: Ms365Assessment[];
	readonly currentActiveMs365Assessment: Ms365Assessment | null;
	readonly ms365AssessmentToBeInserted: string | null;
}

export const initialAssessmentsState: AssessmentsState = {
	// Manual assessments and assessments added to the document
	isAssessmentLoading: false,
	isAssessmentLogLoading: false,
	assessmentList: new Array<Assessment>(),
	currentActiveAssessment: null,
	manualAssessmentDefaults: new Array<AssessmentVulnerabilityData>(),
	assessmentRiskMatrixData: null,
	assessmentRiskBarScore: null,
	assessmentListingData: null,

	// MS 365 Assessments
	isMs365AssessmentsLoading: false,
	ms365AssessmentsModalOpen: false,
	ms365AssessmentsList: new Array<Ms365Assessment>(),
	currentActiveMs365Assessment: null,
	ms365AssessmentToBeInserted: null
};

export const AssessmentsReducer: Reducer<
	AssessmentsState,
	AssessmentsActions
> = (
	state: AssessmentsState = initialAssessmentsState,
	action: AssessmentsActions
): AssessmentsState => {
	switch (action.type) {
		case AssessmentsActionTypes.SHOW_ASSESSMENTS_LOADER: {
			return {
				...state,
				isAssessmentLoading: true
			};
		}
		case AssessmentsActionTypes.HIDE_ASSESSMENTS_LOADER: {
			return {
				...state,
				isAssessmentLoading: false
			};
		}
		case AssessmentsActionTypes.SHOW_ASSESSMENT_LOG_LOADER: {
			return {
				...state,
				isAssessmentLogLoading: true
			};
		}
		case AssessmentsActionTypes.HIDE_ASSESSMENT_LOG_LOADER: {
			return {
				...state,
				isAssessmentLogLoading: false
			};
		}
		case AssessmentsActionTypes.ASSESSMENTS_REQUESTED: {
			return state;
		}
		case AssessmentsActionTypes.ASSESSMENTS_SUCCESSFUL: {
			return {
				...state,
				assessmentList: action.assessmentList
			};
		}
		case AssessmentsActionTypes.ASSESSMENTS_FAILED: {
			return state;
		}
		case AssessmentsActionTypes.ASSESSMENT_DETAILS_REQUESTED: {
			return state;
		}
		case AssessmentsActionTypes.ASSESSMENT_DETAILS_SUCCESSFUL: {
			return {
				...state,
				currentActiveAssessment: action.currentActiveAssessment,
				assessmentRiskMatrixData:
					action.currentActiveAssessment.risk_matrix_cell_data ||
					null,
				assessmentRiskBarScore:
					action.currentActiveAssessment.risk_bar_score || null,
				assessmentListingData:
					action.currentActiveAssessment.risk_listing_data || null
			};
		}
		case AssessmentsActionTypes.ASSESSMENT_DETAILS_FAILED: {
			return state;
		}
		case AssessmentsActionTypes.EDIT_ASSESSMENTS_REQUESTED: {
			return state;
		}
		case AssessmentsActionTypes.EDIT_ASSESSMENTS_SUCCESSFUL: {
			return {
				...state,
				currentActiveAssessment: action.currentActiveAssessment,
				assessmentRiskMatrixData:
					action.currentActiveAssessment.risk_matrix_cell_data ||
					null,
				assessmentRiskBarScore:
					action.currentActiveAssessment.risk_bar_score || null,
				assessmentListingData:
					action.currentActiveAssessment.risk_listing_data || null
			};
		}
		case AssessmentsActionTypes.EDIT_ASSESSMENTS_FAILED: {
			return state;
		}
		case AssessmentsActionTypes.ADD_ASSESSMENT_REQUESTED: {
			return state;
		}
		case AssessmentsActionTypes.ADD_ASSESSMENT_SUCCESSFUL: {
			return {
				...state,
				assessmentList: [action.addAssessment].concat(
					state.assessmentList
				)
			};
		}
		case AssessmentsActionTypes.ADD_ASSESSMENT_FAILED: {
			return state;
		}
		case AssessmentsActionTypes.ASSESSMENTS_RESET: {
			return {
				...state,
				assessmentList: new Array<Assessment>()
			};
		}
		case AssessmentsActionTypes.ASSESSMENT_DETAILS_RESET: {
			return {
				...state,
				currentActiveAssessment: null,
				assessmentRiskBarScore: null,
				assessmentRiskMatrixData: null,
				assessmentListingData: null
			};
		}
		case AssessmentsActionTypes.MANUAL_ASSESSMENT_DEFAULTS_FAILED: {
			return state;
		}
		case AssessmentsActionTypes.MANUAL_ASSESSMENT_DEFAULTS_REQUESTED: {
			return state;
		}
		case AssessmentsActionTypes.MANUAL_ASSESSMENT_DEFAULTS_SUCCESSFUL: {
			return {
				...state,
				manualAssessmentDefaults: action.manualAssessmentDefaults
			};
		}
		case AssessmentsActionTypes.EDIT_ASSESSMENT_DATA_LOG_FAILED: {
			return state;
		}
		case AssessmentsActionTypes.EDIT_ASSESSMENT_DATA_LOG_REQUESTED: {
			return state;
		}
		case AssessmentsActionTypes.EDIT_ASSESSMENT_DATA_LOG_SUCCESSFUL: {
			if (state.currentActiveAssessment) {
				const editedLog = action.editAssessmentLog
					.data as AssessmentVulnerabilityData;
				if (editedLog.cloud_assessment_type === null) {
					const existingLogs =
						state.currentActiveAssessment.manual_assessment_logs
							.data;
					const newLogs = existingLogs.map(eL =>
						eL.id === action.editAssessmentLog.id
							? action.editAssessmentLog
							: eL
					);
					return {
						...state,
						currentActiveAssessment: {
							...state.currentActiveAssessment,
							manual_assessment_logs: { data: newLogs }
						},
						assessmentRiskMatrixData:
							action.editAssessmentLog.risk_matrix_cell_data ||
							null,
						assessmentRiskBarScore:
							action.editAssessmentLog.risk_bar_score || null,
						assessmentListingData:
							action.editAssessmentLog.risk_listing_data || null
					};
				} else if (
					editedLog.cloud_assessment_type ===
					CloudAssessmentType.Ms365
				) {
					const existingLogs =
						state.currentActiveAssessment.assessment_logs.data;
					const newLogs = existingLogs.map(eL =>
						eL.id === action.editAssessmentLog.id
							? action.editAssessmentLog
							: eL
					);
					return {
						...state,
						currentActiveAssessment: {
							...state.currentActiveAssessment,
							assessment_logs: { data: newLogs }
						},
						assessmentRiskMatrixData:
							action.editAssessmentLog.risk_matrix_cell_data ||
							null,
						assessmentRiskBarScore:
							action.editAssessmentLog.risk_bar_score || null,
						assessmentListingData:
							action.editAssessmentLog.risk_listing_data || null
					};
				}
			}

			return state;
		}
		case AssessmentsActionTypes.ADD_ASSESSMENT_DATA_LOG_FAILED: {
			return state;
		}
		case AssessmentsActionTypes.ADD_ASSESSMENT_DATA_LOG_REQUESTED: {
			return state;
		}
		case AssessmentsActionTypes.ADD_ASSESSMENT_DATA_LOG_SUCCESSFUL: {
			if (!state.currentActiveAssessment) {
				return state;
			}
			const newlyAddedLog = action.addAssessmentLog
				.data as AssessmentVulnerabilityData;
			if (newlyAddedLog.cloud_assessment_type === null) {
				const existingLogs =
					state.currentActiveAssessment.manual_assessment_logs.data;
				const newLogs = [action.addAssessmentLog, ...existingLogs];
				return {
					...state,
					currentActiveAssessment: {
						...state.currentActiveAssessment,
						manual_assessment_logs: { data: newLogs },
						risk_matrix_cell_data:
							action.addAssessmentLog.risk_matrix_cell_data,
						risk_bar_score: action.addAssessmentLog.risk_bar_score
					},
					assessmentRiskBarScore:
						action.addAssessmentLog.risk_bar_score || null,
					assessmentRiskMatrixData:
						action.addAssessmentLog.risk_matrix_cell_data || null,
					assessmentListingData:
						action.addAssessmentLog.risk_listing_data || null
				};
			} else if (
				newlyAddedLog.cloud_assessment_type ===
				CloudAssessmentType.Ms365
			) {
				const existingLogs =
					state.currentActiveAssessment.assessment_logs.data;
				const newLogs = [action.addAssessmentLog, ...existingLogs];
				return {
					...state,
					currentActiveAssessment: {
						...state.currentActiveAssessment,
						assessment_logs: { data: newLogs },
						risk_matrix_cell_data:
							action.addAssessmentLog.risk_matrix_cell_data,
						risk_bar_score: action.addAssessmentLog.risk_bar_score
					},
					assessmentRiskBarScore:
						action.addAssessmentLog.risk_bar_score || null,
					assessmentRiskMatrixData:
						action.addAssessmentLog.risk_matrix_cell_data || null,
					assessmentListingData:
						action.addAssessmentLog.risk_listing_data || null
				};
			} else {
				return state;
			}
		}
		case AssessmentsActionTypes.DELETE_ASSESSMENT_DATA_LOG_SUCCESSFUL: {
			if (state.currentActiveAssessment) {
				const existingManualLogs =
					state.currentActiveAssessment.manual_assessment_logs.data;
				const existingAutomatedLogs =
					state.currentActiveAssessment.assessment_logs.data;
				const newManualAssessmentLogs = existingManualLogs.filter(
					log => log.id !== action.deleteAssessmentLog.id
				);
				const newAutomatedAssessmentLogs = existingAutomatedLogs.filter(
					log => log.id !== action.deleteAssessmentLog.id
				);
				return {
					...state,
					currentActiveAssessment: {
						...state.currentActiveAssessment,
						manual_assessment_logs: {
							data: newManualAssessmentLogs
						},
						assessment_logs: { data: newAutomatedAssessmentLogs }
					}
				};
			} else {
				return state;
			}
		}
		case AssessmentsActionTypes.CREATE_ASSESSMENT_CATEGORY_REQUESTED: {
			return state;
		}
		case AssessmentsActionTypes.CREATE_ASSESSMENT_CATEGORY_FAILED: {
			return state;
		}
		case AssessmentsActionTypes.CREATE_ASSESSMENT_CATEGORY_SUCCESSFUL: {
			if (state.currentActiveAssessment) {
				return {
					...state,
					currentActiveAssessment: {
						...state.currentActiveAssessment,
						issue_types_list: [
							...action.currentActiveAssessment.issue_types_list
						]
					}
				};
			} else {
				return state;
			}
		}
		case AssessmentsActionTypes.UPDATE_ASSESSMENT_CATEGORY_FAILED: {
			return state;
		}
		case AssessmentsActionTypes.UPDATE_ASSESSMENT_CATEGORY_REQUESTED: {
			return state;
		}
		case AssessmentsActionTypes.UPDATE_ASSESSMENT_CATEGORY_SUCCESS: {
			if (state.currentActiveAssessment) {
				return {
					...state,
					currentActiveAssessment: {
						...state.currentActiveAssessment,
						issue_types_list: [
							...action.currentActiveAssessment.issue_types_list
						],
						manual_assessment_logs: {
							data: [
								...action.currentActiveAssessment
									.manual_assessment_logs.data
							]
						},
						assessment_logs: {
							data: [
								...action.currentActiveAssessment
									.assessment_logs.data
							]
						}
					},
					assessmentListingData:
						action.currentActiveAssessment.risk_listing_data || null
				};
			} else {
				return state;
			}
		}
		case AssessmentsActionTypes.DELETE_ASSESSMENT_CATEGORY_FAILED: {
			return state;
		}
		case AssessmentsActionTypes.DELETE_ASSESSMENT_CATEGORY_REQUESTED: {
			return state;
		}
		case AssessmentsActionTypes.DELETE_ASSESSMENT_CATEGORY_SUCCESS: {
			if (state.currentActiveAssessment) {
				return {
					...state,
					currentActiveAssessment: {
						...state.currentActiveAssessment,
						issue_types_list: [
							...action.currentActiveAssessment.issue_types_list
						],
						manual_assessment_logs: {
							data: [
								...(action.currentActiveAssessment
									.manual_assessment_logs?.data ?? [])
							]
						},
						assessment_logs: {
							data: [
								...(action.currentActiveAssessment
									.assessment_logs?.data ?? [])
							]
						}
					},
					assessmentListingData:
						action.currentActiveAssessment.risk_listing_data ?? null
				};
			} else {
				return state;
			}
		}
		case AssessmentsActionTypes.REORDER_ASSESSMENT_CATEGORIES_FAILED: {
			return state;
		}
		case AssessmentsActionTypes.REORDER_ASSESSMENT_CATEGORIES_REQUESTED: {
			return state;
		}
		case AssessmentsActionTypes.REORDER_ASSESSMENT_CATEGORIES_SUCCESS: {
			if (state.currentActiveAssessment) {
				return {
					...state,
					currentActiveAssessment: {
						...state.currentActiveAssessment,
						issue_types_list: [
							...action.currentActiveAssessment.issue_types_list
						]
					},
					assessmentListingData:
						action.currentActiveAssessment.risk_listing_data ?? null
				};
			} else {
				return state;
			}
		}
		case AssessmentsActionTypes.SHOW_MS365_ASSESSMENTS_LOADER: {
			return {
				...state,
				isMs365AssessmentsLoading: true
			};
		}
		case AssessmentsActionTypes.HIDE_MS365_ASSESSMENTS_LOADER: {
			return {
				...state,
				isMs365AssessmentsLoading: false
			};
		}
		case AssessmentsActionTypes.MS365_ASSESSMENTS_MODAL_OPENED: {
			return {
				...state,
				ms365AssessmentsModalOpen: true
			};
		}
		case AssessmentsActionTypes.MS365_ASSESSMENTS_MODAL_CLOSED: {
			return {
				...state,
				ms365AssessmentsModalOpen: false
			};
		}
		case AssessmentsActionTypes.MS365_ASSESSMENTS_LIST_REQUESTED: {
			return state;
		}
		case AssessmentsActionTypes.MS365_ASSESSMENTS_LIST_SUCCESS: {
			return {
				...state,
				ms365AssessmentsList: action.ms365AssessmentsList
			};
		}
		case AssessmentsActionTypes.MS365_ASSESSMENTS_LIST_FAILED: {
			return state;
		}
		case AssessmentsActionTypes.MS365_ASSESSMENTS_RESET: {
			return {
				...state,
				ms365AssessmentsList: [],
				currentActiveMs365Assessment: null
			};
		}
		case AssessmentsActionTypes.GENERATE_MS365_ASSESSMENT_REQUESTED: {
			return state;
		}
		case AssessmentsActionTypes.GENERATE_MS365_ASSESSMENT_SUCCESS: {
			return {
				...state,
				currentActiveMs365Assessment:
					action.currentActiveMs365Assessment
			};
		}
		case AssessmentsActionTypes.GENERATE_MS365_ASSESSMENT_FAILED: {
			return state;
		}
		case AssessmentsActionTypes.PREVIEW_MS365_ASSESSMENT_REQUESTED: {
			return state;
		}
		case AssessmentsActionTypes.PREVIEW_MS365_ASSESSMENT_SUCCESS: {
			return {
				...state,
				currentActiveMs365Assessment:
					action.currentActiveMs365Assessment
			};
		}
		case AssessmentsActionTypes.PREVIEW_MS365_ASSESSMENT_FAILED: {
			return state;
		}
		case AssessmentsActionTypes.QUEUE_MS365_ASSESSMENT_TO_ADD_TO_DOC: {
			return {
				...state,
				ms365AssessmentToBeInserted: action.ms365AssessmentId
			};
		}
		case AssessmentsActionTypes.DEQUEUE_MS365_ASSESSMENT_TO_ADD_TO_DOC: {
			return {
				...state,
				ms365AssessmentToBeInserted: null
			};
		}
		case AssessmentsActionTypes.INSERT_MS365_INTO_DOC_REQUESTED: {
			return state;
		}
		case AssessmentsActionTypes.INSERT_MS365_INTO_DOC_SUCCESS: {
			return {
				...state,
				currentActiveAssessment: action.currentActiveAssessment,
				assessmentRiskMatrixData:
					action.currentActiveAssessment.risk_matrix_cell_data ||
					null,
				assessmentRiskBarScore:
					action.currentActiveAssessment.risk_bar_score || null,
				assessmentListingData:
					action.currentActiveAssessment.risk_listing_data || null,
				ms365AssessmentToBeInserted: null
			};
		}
		case AssessmentsActionTypes.INSERT_MS365_INTO_DOC_FAILED: {
			return state;
		}
		default: {
			return state;
		}
	}
};

export default AssessmentsReducer;
